import React, { useState } from 'react';
import { ImageIcon } from 'lucide-react';
import { Container, Section } from '../components/ui/Section';
import { PageHeader } from '../components/ui/PageHeader';
import { PhotoGallery } from '../components/photos/PhotoGallery';
import { EmptyState } from '../components/ui/EmptyState';
import { ButtonExternal } from '../components/ui/Button';
import { Reveal } from '../components/motion/Reveal';
import { FloatingPhoto } from '../components/motion/FloatingPhoto';
import { DoodleStar } from '../components/brand/Doodles';
import { albumMeta, currentYearPhotos, pastPhotos, type AlbumName } from '../data/photos';
import { externalLinks } from '../data/site';
type Filter = 'All' | AlbumName;
const currentFilters: Filter[] = ['All', 'Summer ’26', 'Fall ’26', 'Spring ’27'];
const featuredPhoto = currentYearPhotos[0];
export function Photos() {
  const [filter, setFilter] = useState<Filter>('All');
  const visible = filter === 'All' ? currentYearPhotos : currentYearPhotos.filter(photo => photo.album === filter);
  const emptyAlbum = filter !== 'All' ? albumMeta.find(album => album.name === filter) : undefined;
  return <>
      <PageHeader eyebrow="Photos" title="Life at BCM" intro="Explore real moments from worship nights, campus outreach, recreation, community groups, and time spent together." data-id="element-654" data-mp="2kjba51hvrett" />

      {/* Featured photo */}
      <Section className="bg-[#fffdf6] py-14 sm:py-18" labelledBy="featured-heading" data-id="element-655" data-mp="2tiy0ms92n083">
        <Container className="grid items-center gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:gap-12" data-id="element-656" data-mp="2olfybkpk7j92">
          <Reveal className="relative" data-id="element-657" data-mp="33ac4tdbywo7k">
            <FloatingPhoto range={5} duration={8} data-id="element-658" data-mp="3399izjti5byi">
              <figure className="relative overflow-hidden rounded-[20px] border-2 border-brand-navy bg-brand-blue" data-id="element-659" data-mp="2ya6afpebcnls">
                {featuredPhoto.src ? <img src={featuredPhoto.src} alt={featuredPhoto.alt} className="aspect-[16/10] w-full object-cover" data-id="element-660" data-mp="3caqxixr9h5l3" /> : <div className="flex aspect-[16/10] w-full flex-col items-center justify-center gap-3 px-6 text-center" data-id="element-661" data-mp="3kb6l30w14s2b">
                    <ImageIcon aria-hidden="true" className="h-8 w-8 text-brand-navy" data-id="element-662" data-mp="3p9q7f2ejc5v7" />
                    <p className="font-meta text-[15px] font-semibold text-brand-navy" data-id="element-663" data-mp="3u0q6ad77sy0c">
                      Add Image here
                    </p>
                  </div>}
                {featuredPhoto.src ? <figcaption className="border-t-2 border-brand-navy bg-white px-5 py-3 font-meta text-[13px] text-ink" data-id="element-664" data-mp="6v8zds68q8ac">
                    {featuredPhoto.caption ?? featuredPhoto.subject}
                  </figcaption> : null}
              </figure>
            </FloatingPhoto>
          </Reveal>

          <Reveal x={16} delay={0.12} className="relative" data-id="element-665" data-mp="2dob2gwac0zd2">
            <DoodleStar className="absolute -left-3 -top-6 h-9 w-9" color="var(--bcm-yellow)" data-id="element-666" data-mp="2ilb85639s03n" />
            <h2 id="featured-heading" className="font-serif text-2xl font-semibold leading-snug text-brand-navy sm:text-3xl" data-id="element-667" data-mp="zqglhe8euzob">
              2026–27 at UCF BCM
            </h2>
            <p className="mt-4 font-sans text-[16px] leading-relaxed text-ink" data-id="element-668" data-mp="qxlot8x0eca5">
              New photographs will be added throughout Welcome Week, worship nights, and the school
              year.
            </p>
            <ul className="mt-5 space-y-2" data-id="element-669" data-mp="1io9n8d7at3k4">
              {albumMeta.map(album => <li key={album.name} className="font-meta text-[13px] text-ink" data-id="element-670" data-mp="1ep0u9ak319pm">
                  <span className="font-semibold text-brand-navy" data-id="element-671" data-mp="19od0wi8dsgs8">{album.name}</span> —{' '}
                  {album.status}
                </li>)}
            </ul>
            <ButtonExternal href={externalLinks.photoAlbum} className="mt-7" data-id="element-672" data-mp="3uf3ub9pb777e">
              Open Full Photo Album
            </ButtonExternal>
          </Reveal>
        </Container>
      </Section>

      {/* Current year gallery */}
      <Section className="bcm-paper py-14 sm:py-18" labelledBy="current-year-heading" data-id="element-673" data-mp="1h72hwswwcsio">
        <Container data-id="element-674" data-mp="1lykobwhovlb1">
          <Reveal data-id="element-675" data-mp="1lz2o333k8hnp">
            <h2 id="current-year-heading" className="font-display text-3xl font-extrabold leading-tight tracking-tight text-brand-navy sm:text-4xl" data-id="element-676" data-mp="1lzm0k4afyduc">
              Browse this year by semester
            </h2>
          </Reveal>

          <div role="group" aria-label="Filter photos by semester" className="mt-6 flex flex-wrap gap-2" data-id="element-677" data-mp="20v1t24vdi1om">
            {currentFilters.map(option => {
            const isActive = filter === option;
            return <button key={option} type="button" aria-pressed={isActive} onClick={() => setFilter(option)} className={`inline-flex min-h-[44px] items-center rounded-full border-2 border-brand-navy px-5 font-meta text-[13px] font-semibold transition-colors ${isActive ? 'bg-brand-navy text-white' : 'bg-white text-brand-navy hover:bg-brand-tan'}`} data-id="element-678" data-mp="1vvyqqs6s29kc">
                  {option}
                </button>;
          })}
          </div>

          <div className="mt-8" data-id="element-679" data-mp="h0fkjr2o78ln">
            {visible.length > 0 ? <PhotoGallery photos={visible} data-id="element-680" data-mp="3pefusrqaw1v8" /> : <Reveal data-id="element-681" data-mp="3pcl7j6rk3xnz">
                <EmptyState label={emptyAlbum?.name ?? 'Coming soon'} title="No photos in this folder yet" copy={emptyAlbum?.message ?? 'Photos will be added to this folder as the semester goes on.'} tone="white" data-id="element-682" data-mp="3ldm3dodng4xl">
                  <ButtonExternal href={externalLinks.photoAlbum} variant="secondary" data-id="element-683" data-mp="36md9v46mlzf6">
                    Open Full Photo Album
                  </ButtonExternal>
                </EmptyState>
              </Reveal>}
          </div>
        </Container>
      </Section>

      {/* Past moments */}
      <Section className="bg-[#fffdf6] py-14 sm:py-18" labelledBy="past-moments-heading" data-id="element-684" data-mp="1aj8mbzmn03af">
        <Container data-id="element-685" data-mp="1fe4o3rhoe50q">
          <Reveal data-id="element-686" data-mp="1kb4ts1am55rb">
            <h2 id="past-moments-heading" className="font-serif text-3xl font-semibold leading-tight text-brand-navy sm:text-4xl" data-id="element-687" data-mp="1kbmnor9sz7w2">
              Past BCM moments
            </h2>
            <p className="mt-4 max-w-2xl font-sans text-[16px] leading-relaxed text-ink" data-id="element-688" data-mp="rnie3uo42ao3">
              Explore worship nights, community groups, campus outreach, recreation, trips, and
              friendships from the previous school year.
            </p>
          </Reveal>

          <div className="mt-8" data-id="element-689" data-mp="2818kx96m7d1f">
            <PhotoGallery photos={pastPhotos} data-id="element-690" data-mp="27zxl1kgaipfl" />
          </div>

          <Reveal delay={0.1} data-id="element-691" data-mp="2c1rf39vzcv6s">
            <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-692" data-mp="2c0zh5sewuldc">
              <ButtonExternal href={externalLinks.pastPhotoAlbum} data-id="element-693" data-mp="fgtu8gl7dkg">
                View Previous-Year Photos
              </ButtonExternal>
              <ButtonExternal href={externalLinks.photoAlbum} variant="secondary" data-id="element-694" data-mp="3sj9nu5wvaaam">
                Open Current Photo Album
              </ButtonExternal>
            </div>
            <p className="mt-6 font-meta text-[12.5px] text-ink" data-id="element-695" data-mp="3itq1gq9y3yb0">
              Photos load as you scroll. Select any photo to open a larger view — use the arrow keys
              to move between photos and Escape to close.
            </p>
          </Reveal>
        </Container>
      </Section>
    </>;
}